/**
 * @file ModernDashboard/index.jsx
 * @description Attractive KDS-style dashboard with greeting, calendar, metrics, and charts
 */

import React, { useEffect, useMemo, useState } from "react";
import GreetingCard from "./GreetingCard";
import CalendarPanel from "./CalendarPanel";
import ServerOrders from "./ServerOrders";
import MetricsBar from "./MetricsBar";
import Charts from "./Charts";
import { apiFetch } from "../../../utils/api";

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      "px-3 py-1.5 rounded-full text-sm font-semibold transition",
      active
        ? "bg-blue-600 text-white shadow"
        : "bg-white/70 text-gray-700 hover:bg-white",
    ].join(" ")}
  >
    {children}
  </button>
);

const ModernDashboard = () => {
  const [ordersByDay, setOrdersByDay] = useState([]);
  const [avgBumpTime, setAvgBumpTime] = useState([]);
  const [itemsData, setItemsData] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // NEW: hierarchy rows for tabs
  const [hierRows, setHierRows] = useState([]);

  // NEW: selected tab state (filters will be used later)
  const [selectedSiteId, setSelectedSiteId] = useState("all");
  const [selectedLocationId, setSelectedLocationId] = useState("all");
  const [selectedSublocationId, setSelectedSublocationId] = useState("all");

  const getLast30DayRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);

    const toYmd = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    return { startDate: toYmd(start), endDate: toYmd(end) };
  };

  useEffect(() => {
    fetchHierarchy();
  }, []);

  useEffect(() => {
    fetchDashboardData({
      site_id: selectedSiteId !== "all" ? selectedSiteId : "",
      location_id: selectedLocationId !== "all" ? selectedLocationId : "",
      sublocation_id:
        selectedSublocationId !== "all" ? selectedSublocationId : "",
    });
  }, [selectedSiteId, selectedLocationId, selectedSublocationId]);

  const fetchHierarchy = async () => {
    try {
      const res = await apiFetch("/dashboard/hierarchy");
      const rows = await res.json();
      setHierRows(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error(" Failed to load dashboard hierarchy:", e);
      setHierRows([]);
    }
  };

  const fetchDashboardData = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      const { startDate, endDate } = getLast30DayRange();

      if (filters.site_id) params.append("site_id", String(filters.site_id));
      if (filters.location_id)
        params.append("location_id", String(filters.location_id));
      if (filters.sublocation_id)
        params.append("sublocation_id", String(filters.sublocation_id));
      params.append("startDate", startDate);
      params.append("endDate", endDate);
      const query = params.toString();

      const [ordersRaw, bumpRaw, itemsRaw, totalItemsRaw] = await Promise.all([
        apiFetch(`/updates-reports/orders-by-day${query ? `?${query}` : ""}`),
        apiFetch(`/updates-reports/avg-bump-time${query ? `?${query}` : ""}`),
        apiFetch(`/updates-reports/top-items-by-count${query ? `?${query}` : ""}`),
        apiFetch(`/updates-reports/total-items-sold${query ? `?${query}` : ""}`),
      ]);

      const orders = await ordersRaw.json();
      const bump = await bumpRaw.json();
      const items = await itemsRaw.json();
      const totalItemsPayload = await totalItemsRaw.json();

      setOrdersByDay(Array.isArray(orders) ? orders : []);
      setAvgBumpTime(Array.isArray(bump) ? bump : []);
      setItemsData(Array.isArray(items) ? items : []);

      const totalOrderCount = (orders || []).reduce(
        (sum, day) => sum + (day.total_orders || 0),
        0
      );
      setTotalOrders(totalOrderCount);

      setTotalItems(Number(totalItemsPayload?.total_items_sold || 0));
    } catch (e) {
      console.error(" Failed to load dashboard data:", e);
    }
  };

  // Build tab option lists from vw_sitelocation rows
  const sites = useMemo(() => {
    const map = new Map();
    for (const r of hierRows) {
      if (r.site_id == null) continue;
      if (!map.has(r.site_id)) map.set(r.site_id, r.site_name || `Site ${r.site_id}`);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [hierRows]);

  const locations = useMemo(() => {
    const map = new Map();
    for (const r of hierRows) {
      if (r.location_id == null) continue;

      // when a site is selected, only show that site's locations
      if (selectedSiteId !== "all" && Number(r.site_id) !== Number(selectedSiteId)) continue;

      if (!map.has(r.location_id))
        map.set(r.location_id, r.location_name || `Location ${r.location_id}`);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [hierRows, selectedSiteId]);

  const sublocations = useMemo(() => {
    const map = new Map();
    for (const r of hierRows) {
      if (r.sublocation_id == null) continue;

      if (selectedSiteId !== "all" && Number(r.site_id) !== Number(selectedSiteId)) continue;
      if (
        selectedLocationId !== "all" &&
        Number(r.location_id) !== Number(selectedLocationId)
      )
        continue;

      if (!map.has(r.sublocation_id))
        map.set(
          r.sublocation_id,
          r.sublocation_name || `Sublocation ${r.sublocation_id}`
        );
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [hierRows, selectedSiteId, selectedLocationId]);

  // Keep lower-level selections valid when upper changes
  useEffect(() => {
    setSelectedLocationId("all");
    setSelectedSublocationId("all");
  }, [selectedSiteId]);

  useEffect(() => {
    setSelectedSublocationId("all");
  }, [selectedLocationId]);

  const SquareTab = ({ active, onClick, children, row, subtle, subtle2 }) => {
    const base =
      "px-4 py-2 text-sm font-semibold border-r border-gray-200 transition whitespace-nowrap";

    const inactiveBg = subtle
      ? "bg-white hover:bg-gray-50 text-gray-700"
      : subtle2
        ? "bg-white hover:bg-gray-50 text-gray-700"
        : "bg-gray-50 hover:bg-white text-gray-700";

    const activeBg = subtle
      ? "bg-blue-50 text-blue-900"
      : subtle2
        ? "bg-blue-50 text-blue-900"
        : "bg-blue-600 text-white";

    // remove outer border “gaps” since the row supplies the separator borders
    const rowStyling = row ? "rounded-none border-t-0 border-l-0 border-b-0" : "";

    return (
      <button
        type="button"
        onClick={onClick}
        className={[base, rowStyling, active ? activeBg : inactiveBg].join(" ")}
      >
        {children}
      </button>
    );
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-6 space-y-6 font-sans">
      {/* Grouped Tabs */}
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        {/* Sites row */}
        <div className="flex flex-wrap border-b bg-gray-50">
          <SquareTab
            row
            active={selectedSiteId === "all"}
            onClick={() => setSelectedSiteId("all")}
          >
            All Sites
          </SquareTab>

          {sites.map((s) => (
            <SquareTab
              key={s.id}
              row
              active={Number(selectedSiteId) === Number(s.id)}
              onClick={() => setSelectedSiteId(s.id)}
            >
              {s.name}
            </SquareTab>
          ))}
        </div>

        {/* Locations row (only when a site is selected) */}
        {selectedSiteId !== "all" && (
          <div className="flex flex-wrap border-b bg-white">
            <SquareTab
              row
              subtle
              active={selectedLocationId === "all"}
              onClick={() => setSelectedLocationId("all")}
            >
              All Locations
            </SquareTab>

            {locations.map((l) => (
              <SquareTab
                key={l.id}
                row
                subtle
                active={Number(selectedLocationId) === Number(l.id)}
                onClick={() => setSelectedLocationId(l.id)}
              >
                {l.name}
              </SquareTab>
            ))}
          </div>
        )}

        {/* Sublocations row (only when a location is selected) */}
        {selectedSiteId !== "all" && selectedLocationId !== "all" && (
          <div className="flex flex-wrap bg-white">
            <SquareTab
              row
              subtle2
              active={selectedSublocationId === "all"}
              onClick={() => setSelectedSublocationId("all")}
            >
              All Sublocations
            </SquareTab>

            {sublocations.map((sl) => (
              <SquareTab
                key={sl.id}
                row
                subtle2
                active={Number(selectedSublocationId) === Number(sl.id)}
                onClick={() => setSelectedSublocationId(sl.id)}
              >
                {sl.name}
              </SquareTab>
            ))}
          </div>
        )}
      </div>


      {/* Existing dashboard content (same info on all tabs for now) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <GreetingCard />
        <CalendarPanel ordersByDay={ordersByDay} />
        <ServerOrders
          siteId={selectedSiteId}
          locationId={selectedLocationId}
          sublocationId={selectedSublocationId}
        />
      </div>

      <MetricsBar
        totalOrders={totalOrders}
        avgBumpTime={avgBumpTime}
        totalItems={totalItems}
      />

      <Charts
        ordersByDay={ordersByDay}
        itemsData={itemsData}
        avgBumpTime={avgBumpTime}
      />
    </div>
  );
};

export default ModernDashboard;
